import * as React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Calculator } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { useFormSessionTracking, trackStep, readDealerCode, readEntryPage, AMOUNTS_KEY } from '../../lib/formSession';
import { DEFAULT_TERM_MONTHS, weeklyRepayment } from '../../lib/finance';

const currency = (n) =>
  new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD', maximumFractionDigits: 0 }).format(n);

// The calculator sits BEFORE step 1 and asks nothing, so it reports step 0. `/api/forms/session`
// accepts a zero step for exactly this reason — see routers/forms.py.
const CALCULATOR_STEP = 0;
const TOTAL = 3;

// Where the calculator's numbers live between screens.
//
// sessionStorage rather than component state: going Back from the form — whether by the button or
// the browser's own back — remounts this component, and re-reading defaults would silently discard
// the price and deposit they just set and make them enter it all again.
const DEFAULTS = { borrow_amount: 35000, deposit_amount: 5000 };

function readAmounts(fallback) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(AMOUNTS_KEY) || 'null');
    if (saved && Number(saved.borrow_amount) > 0) {
      return {
        borrow_amount: Number(saved.borrow_amount),
        // `Number(x) || 0` is safe here only because 0 IS the fallback — a saved zero deposit
        // survives, which is the whole point.
        deposit_amount: Number(saved.deposit_amount) || 0,
      };
    }
  } catch {
    /* storage disabled — the defaults are a fine fallback */
  }
  return fallback || DEFAULTS;
}

/**
 * The hero calculator.
 *
 * Just the calculator, as it has always been: two sliders and what it would cost weekly. Continuing
 * carries the numbers into `/quick-start`, which runs the same full-page onboarding screen as
 * `/get-started` over three steps instead of six.
 *
 * The form deliberately does NOT live in this card. The card is a sidebar on a marketing page —
 * fine for two sliders, cramped for name, email, phone and a consent declaration — and an earlier
 * version that stacked the steps underneath made it unreadably tall.
 */
export default function HeroQuickStart({ rate, defaults, vehicleCategory }) {
  const navigate = useNavigate();
  // No `loan_type` here: this screen asks nothing about the product, and seeding a default into
  // the tracked payload would record "wants a vehicle loan" for someone who only moved a slider.
  // `dealer_code` rides along in the tracked form: an abandoned calculator session from a referral
  // link is still that dealer's lead, and `trackStep` reads attribution straight off the form.
  // `defaults` lets a vertical landing page start the sliders somewhere sensible for what that
  // page is about — a supercar page opening at $35k is an immediate signal that the page was not
  // written for the reader. Whatever they set LAST still wins: `readAmounts` reads the figures
  // carried back from a previous visit to the funnel, and overriding those with a page default
  // would silently undo their own input.
  const [form, setForm] = useState(() => ({
    ...(defaults || {}),
    ...readAmounts(defaults),
    dealer_code: readDealerCode(),
    entry_page: readEntryPage(),
    ...(vehicleCategory ? { vehicle_category: vehicleCategory } : {}),
  }));

  // Tracks once they actually move a slider — not on page load. The session id lives in
  // sessionStorage, so `/quick-start` continues the same session rather than starting a new one.
  useFormSessionTracking(React, {
    capture: 'hero',
    step: CALCULATOR_STEP,
    stepKey: 'hero_calculator',
    totalSteps: TOTAL,
    form,
  });

  const set = (k, v) =>
    setForm((p) => {
      const next = { ...p, [k]: v };
      try {
        sessionStorage.setItem(AMOUNTS_KEY, JSON.stringify(next));
      } catch {
        /* storage disabled — the value still applies for this screen */
      }
      return next;
    });
  const loanAmount = form.borrow_amount - form.deposit_amount;
  const weekly = weeklyRepayment(loanAmount, DEFAULT_TERM_MONTHS, rate);

  // The amounts travel in navigation state so they are never re-entered on the next screen.
  const onContinue = () => {
    // Flush the calculator stage before leaving. Tracking is debounced by 1500ms and navigating
    // unmounts the timer, so moving a slider and pressing Continue quickly — which is the fast,
    // engaged path, not an edge case — recorded no calculator stage at all.
    trackStep({
      capture: 'hero',
      step: CALCULATOR_STEP,
      stepKey: 'hero_calculator',
      totalSteps: TOTAL,
      form,
      payload: form,
    });
    navigate('/quick-start', {
      state: {
        borrow_amount: form.borrow_amount,
        deposit_amount: form.deposit_amount,
        // Carried explicitly: navigating drops the query string, and `?dealer=` is how a referral
        // is attributed. Losing it here would silently cost the dealer their commission.
        dealer_code: form.dealer_code,
        // Only set when a vertical page supplied one. Someone who arrived on /finance/utes has
        // already told us what they are shopping for by being there; asking again is the kind of
        // small redundancy that makes a "tailored" page feel like a generic one after all.
        vehicle_category: form.vehicle_category,
      },
    });
  };

  return (
    <div
      className="rounded-2xl border border-white/20 bg-white/10 p-8 backdrop-blur-xl"
      data-testid="hero-quick-start"
    >
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#00D47E]">
          <Calculator className="h-5 w-5 text-black" />
        </div>
        <div className="flex-1">
          <h3 className="font-outfit font-semibold text-white">What would it cost?</h3>
          <p className="text-sm text-gray-400">Move the sliders to see your repayments</p>
        </div>
      </div>

      <div className="space-y-6">
        <Slider
          label="Vehicle Price" value={form.borrow_amount} display={currency(form.borrow_amount)}
          min={5000} max={350000} step={1000} lo="$5k" hi="$350k"
          testId="hero-price"
          onChange={(v) => set('borrow_amount', v)}
        />
        <Slider
          label="Deposit" value={form.deposit_amount} display={currency(form.deposit_amount)}
          min={0} max={Math.round(form.borrow_amount / 2)} step={500} lo="$0"
          hi={currency(Math.round(form.borrow_amount / 2))}
          testId="hero-deposit"
          onChange={(v) => set('deposit_amount', v)}
        />
        <div className="rounded-xl bg-black/40 p-4" data-testid="hero-result">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-gray-400">Loan Amount</span>
            <span className="font-outfit text-xl font-bold text-[#00D47E]">{currency(loanAmount)}</span>
          </div>
          <div className="mt-3 border-t border-white/10 pt-3">
            <span className="text-sm text-gray-400">Estimated Weekly</span>
            <p className="font-outfit text-3xl font-bold text-white" data-testid="hero-weekly">
              {currency(weekly)}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Based on {(rate * 100).toFixed(2)}% over {DEFAULT_TERM_MONTHS} months
            </p>
          </div>
        </div>
      </div>

      <Button
        onClick={onContinue}
        disabled={loanAmount <= 0}
        data-testid="hero-next"
        className="mt-6 w-full rounded-full bg-[#00D47E] py-6 text-base font-semibold text-black hover:bg-[#00D47E]/90"
      >
        Continue <ArrowRight className="ml-1.5 h-4 w-4" />
      </Button>
      <p className="mt-3 text-center text-xs text-gray-500">
        Takes about a minute · a specialist will be in touch
      </p>
    </div>
  );
}

function Slider({ label, value, display, min, max, step, lo, hi, onChange, testId }) {
  return (
    <div>
      <div className="mb-2 flex justify-between text-sm text-gray-400">
        <span>{label}</span>
        <span className="font-medium text-white">{display}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        aria-label={label}
        data-testid={testId}
        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-gray-700 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#00D47E] [&::-webkit-slider-thumb]:shadow-lg"
      />
      <div className="mt-1 flex justify-between text-xs text-gray-500">
        <span>{lo}</span><span>{hi}</span>
      </div>
    </div>
  );
}
